import * as React from "react";
import { cn } from "../../lib/utils";
import { Truncate } from "./Truncate";

/**
 * The label/value row on a record card, and the list that aligns a card's rows
 * into one label column.
 *
 * 🔴 THE DEFECT THIS RETIRES: A FIXED LABEL TRACK IS WRONG FOR EVERY ROW AT
 * ONCE. The shape this replaces was `grid-cols-[11rem_1fr]` on each row
 * INDIVIDUALLY — a 176px label track, whatever the label. Measured against the
 * 57 labels the buyer, listing and submission cards actually carry:
 *
 *   `DOWN PAYMENT AVAILABLE`   202.95px — over by 27, so it WRAPPED
 *   `MANAGEMENT EXPERIENCE`    198.98px — over by 23, so it wrapped too
 *   median label               ~103px   — 73px of the track wasted, on 45 of 57
 *
 * So one number was simultaneously too narrow for the two longest labels and
 * 30-75px too wide for almost every other row, and the value paid for both: on
 * a 357px card the value track was 123px, which set a 513px listing headline to
 * SIX lines and left a 328px email nowhere to go.
 *
 * The label track is therefore CONTENT-SIZED WITH A BOUND — `fit-content(13rem)`
 * — and it lives on the LIST rather than the row, so a card's labels still share
 * one left edge. `fit-content(limit)` is `min(max-content, max(min-content,
 * limit))`: a card of short labels gets a short track, a card with a long one
 * gets up to 208px, and nothing gets more. 13rem clears both outliers above with
 * 5px to spare and costs the other 45 nothing, because `fit-content` sizes to
 * content and not to the bound.
 *
 * SUBGRID, SO A ROW STAYS ONE GRID ITEM. Each row spans both tracks and adopts
 * the list's columns (`grid-cols-subgrid`), rather than dissolving into two
 * items with `display: contents`. That keeps field-to-field spacing (`gap-y` on
 * the list) independent of label-to-value spacing (`gap-x` on the row) — with
 * `contents` they are the same axis and collapse into each other the moment the
 * layout stacks.
 *
 * 🔴 WHY A CONTAINER QUERY HERE WHEN THE TABLE & TILE STANDARD REJECTED ONE FOR
 * THE CARD GRID. §12.2 rejected it because the fallback was a SILENT COLLAPSE:
 * an unsupported browser, or one missing `container-type` on an ancestor, would
 * paint every card grid as a single column. Here the fallback is EXACTLY TODAY'S
 * BEHAVIOUR — the row stays side by side — and this component sets its own
 * container, so there is no ancestor for a caller to forget. A benign fallback
 * and a destructive one do not deserve the same ruling; §12.2 records the
 * distinction rather than being contradicted quietly.
 *
 * 🔴 THE CONTAINER IS A WRAPPER, NOT THE GRID, AND IT HAS TO BE. AN ELEMENT IS
 * NOT ITS OWN CONTAINER: `@container` resolves against the nearest ANCESTOR
 * carrying `container-type`, so putting `container-type: inline-size` and the
 * `@container` rule on the same div compiles, emits correct CSS, applies
 * `container-type` — and then never matches. Measured on the first build of
 * this file: a 300px card kept a two-column `137px 113px` template at every
 * width, with the rule present in the stylesheet and the container established
 * on the element it was meant to style. Nothing fails; the layout simply never
 * stacks.
 *
 * 🔴 THE COLUMN GAP BELONGS TO THE LIST, NOT THE ROW. A subgrid inherits its
 * parent's gutters, and `fit-content()` sizes the track from the items' own
 * contributions — so a gap declared on the ROW is invisible to the parent's
 * track sizing and then eats the track it was not counted in. Measured, same
 * build: `gap-x` on the row made the label's contribution 210.95px against a
 * 208px bound, the track clamped to the bound, the cell got 200px, and
 * `DOWN PAYMENT AVAILABLE` (202.95px) wrapped anyway — the exact defect the
 * 13rem bound was chosen to fix.
 *
 * WHAT THE CALLER OWES: nothing. The value cell carries its own `min-w-0` and
 * clamps through {@link Truncate}, because the row that shipped without
 * either is the row that overflowed 205px into the next card.
 *
 * IT IS A REAL DEFINITION LIST. The list is a `<dl>` and each row a `<div>`
 * holding a `<dt>` and a `<dd>` — the HTML5 grouping shape, which is what makes
 * one row a single grid item without giving up the semantics. It is worth
 * stating because the four counterparty pages this replaced rendered `<dt>` and
 * `<dd>` with NO `<dl>` anywhere above them, which is not a downgrade this
 * change had to avoid so much as invalid markup it happens to fix.
 *
 * 🔴 `break-words`, OR THE ELLIPSIS NEVER APPEARS ON THE VALUES THAT NEED IT
 * MOST. `-webkit-line-clamp` marks the cut by eliding a FOLLOWING line, so it
 * has nothing to elide when the value is a single unbreakable token wider than
 * its track — an email, a URL, an id. The clamp then degrades to a bare
 * `overflow: hidden`, which is the exact defect {@link Truncate}'s own contract
 * opens by naming. Measured in the style guide before this class was added:
 * `d.whitfield+prequalification@northbridge.example` in a 310.77px track
 * rendered as `…@northbridge.ex`, cut mid-token, unmarked. With the token
 * allowed to break it wraps to two lines and the clamp has a third to elide.
 *
 * Scoped to this row rather than to `Truncate`, deliberately: every multi-line
 * clamp in the app has the same gap, and widening it there would change what
 * every existing consumer paints in one pass. The general case is recorded in
 * the Table & Tile Standard §11 instead of being fixed quietly here.
 *
 * 🔴 THE REVEAL IS MEASURED, NOT ASSUMED (`revealOnlyWhenClamped`). `Truncate`
 * makes every value it wraps a tab stop, which is right in a table where almost
 * every cell clamps and wrong here: measured across the buyer and listing cards
 * at their production width, wrapping the values added 18 focusable elements
 * and clamped NONE of them — about forty dead tab stops on a full Overview,
 * standing between a keyboard user and the controls they were reaching for. A
 * value here becomes focusable only once it is measured to overflow.
 */

/** The narrowest a row stays side by side. Below it the value moves under its
 *  label — a 13rem label plus a usable value does not fit a drawer-width card,
 *  and two cramped columns read worse than one honest one. */
const STACK_BELOW = "22rem";

/** The most a label track may take before the label wraps instead. */
const LABEL_MAX = "13rem";

export interface DetailFieldListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  children: React.ReactNode;
}

/**
 * The container for a card's rows. Owns the shared label track, so every
 * {@link DetailField} inside one list lines up.
 */
export function DetailFieldList({ className, children, ...props }: DetailFieldListProps) {
  return (
    // The container. Separate from the grid on purpose — see the note above.
    <div
      className={cn("min-w-0 [container-type:inline-size]", className)}
      data-phx-field-container=""
      data-phx-stack-below={STACK_BELOW}
      {...props}
    >
      <dl
        className={cn(
          "m-0 grid min-w-0 gap-x-space-3 gap-y-space-2",
          "grid-cols-[fit-content(var(--phx-label-max))_minmax(0,1fr)]",
          // Below the threshold there is one track, so a row's subgrid has one
          // column too and its value lands under its label with no further rule.
          "[@container(max-width:22rem)]:grid-cols-1",
        )}
        style={{ "--phx-label-max": LABEL_MAX } as React.CSSProperties}
        data-phx-field-list=""
      >
        {children}
      </dl>
    </div>
  );
}

export interface DetailFieldProps {
  label: string;
  value?: React.ReactNode;
  /**
   * The value, as children.
   *
   * <p>An alias for {@link DetailFieldProps.value}, and it exists for exactly
   * one reason: the four counterparty pages whose hand-rolled copies this
   * replaced all took the value as children, across 90 call sites between them.
   * With this alias their conversion is an import swap and a deleted function —
   * `import { DetailField as Field }` — instead of 90 hand edits, each of which
   * is a chance to move a value onto the wrong row.</p>
   *
   * <p>`value` wins if both are given; nothing passes both.</p>
   */
  children?: React.ReactNode;
  /**
   * Give the value the whole row, with its label above it.
   *
   * <p>The rule, so this is not a per-field taste call: <b>any field whose
   * editor is a textarea is wide.</b> A paragraph does not belong in a 300px
   * column beside a 200px label — the listing headline set to six lines that
   * way. Callers driven by a section config should pass
   * {@link isWideFieldType}'s answer rather than deciding per field.</p>
   */
  wide?: boolean;
  /** How many lines the value may take before it clamps. Defaults to 2. */
  lines?: 1 | 2 | 3;
  "data-testid"?: string;
}

/**
 * One labelled value.
 *
 * An absent value renders an em dash rather than being omitted: a field that
 * disappears when empty makes the reader wonder whether the record has that
 * field at all, and "we do not know" is itself information on a CRM record. The
 * dash is deliberately NOT wrapped for truncation — there is nothing to reveal,
 * and a tab stop on an em dash is a tab stop that costs a keyboard reader a
 * press for nothing.
 */
export function DetailField({
  label,
  value,
  children,
  wide = false,
  lines = 2,
  "data-testid": testId,
}: DetailFieldProps) {
  const shown = value ?? children;
  const empty = shown === null || shown === undefined || shown === "";
  const labelCell = (
    <dt className="m-0 text-caption uppercase tracking-wide text-gray-mid">{label}</dt>
  );
  const valueCell = (
    <dd className="m-0 min-w-0 text-small text-onyx">
      {empty ? (
        <span className="text-gray-mid">—</span>
      ) : typeof shown === "string" ? (
        // Only a plain string can be clamped-and-revealed: `Truncate` reveals
        // the same text it renders, and it cannot read a `<Timestamp>` or a
        // badge back out of a node. A non-string value keeps `min-w-0` and its
        // own wrapping, which is what every element value here already does.
        <Truncate lines={lines} revealOnlyWhenClamped className="break-words">
          {shown}
        </Truncate>
      ) : (
        shown
      )}
    </dd>
  );

  if (wide) {
    return (
      <div
        className="col-span-full grid min-w-0 gap-y-space-1"
        data-phx-field={label}
        data-phx-field-wide=""
        data-testid={testId}
      >
        {labelCell}
        {valueCell}
      </div>
    );
  }

  return (
    <div
      // No `gap-x` here: the subgrid inherits the list's, which is the only
      // gap `fit-content()` counted when it sized the label track.
      className="col-span-full grid min-w-0 grid-cols-subgrid items-baseline"
      data-phx-field={label}
      data-testid={testId}
    >
      {labelCell}
      {valueCell}
    </div>
  );
}

/**
 * Whether a section-config field is a wide row.
 *
 * <p>Named once and shared, because the edit modals compute the same predicate
 * for their own two-column form grid (`BuyerSectionEditModal`,
 * `ListingSectionEditModal`) and a read view that disagreed with its own editor
 * about which fields are wide would be worse than either answer alone.</p>
 */
export function isWideFieldType(field: { type: string; wide?: boolean }): boolean {
  return field.wide === true || field.type === "textarea";
}
